import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Users, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AdminShell } from "./AdminShell";
import { PageHeader, StatusBadge, StatusMenu } from "./parts";

const EMPTY = { identifiant: "", email: "", password: "", first_name: "", last_name: "" };

function ParentDialog({ open, onOpenChange, parent, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!parent;
  useEffect(() => {
    setForm(parent
      ? { identifiant: parent.identifiant, email: parent.email || "", password: "", first_name: parent.first_name, last_name: parent.last_name }
      : EMPTY);
  }, [parent, open]);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Nom et prénom obligatoires.");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/users/${parent.id}`, { first_name: form.first_name, last_name: form.last_name, email: form.email });
      } else {
        await api.post("/admin/parents", { identifiant: form.identifiant, email: form.email || undefined, password: form.password, first_name: form.first_name, last_name: form.last_name });
      }
      toast.success(editing ? "Parent modifié." : "Parent créé.");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="parent-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifier le parent" : "Nouveau parent"}</DialogTitle><DialogDescription>Gérez le compte parent.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prénom *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="parent-firstname-input" /></div>
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="parent-lastname-input" /></div>
          </div>
          {!editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Identifiant *</Label><Input value={form.identifiant} onChange={(e) => setForm({ ...form, identifiant: e.target.value })} data-testid="parent-identifiant-input" /></div>
              <div className="space-y-1.5"><Label>Mot de passe *</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="parent-password-input" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="parent-save-btn">{saving && <Loader2 className="mr-2 animate-spin" size={16} />} Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminParents() {
  const [parents, setParents] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPw, setTempPw] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/users?role=parent");
      setParents(res.data);
    } catch { toast.error("Impossible de charger les parents."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (u, status) => {
    try {
      await api.post(`/admin/users/${u.id}/status`, { status });
      toast.success(status === "supprimé" ? "Compte supprimé (restaurable 30 jours)." : "Statut mis à jour.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetPassword = async (u) => {
    try {
      const res = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPw({ name: `${u.first_name} ${u.last_name}`, pw: res.data.temp_password });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminShell>
      <PageHeader
        title="Parents"
        subtitle="Comptes parents de la plateforme."
        action={<Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-parent-btn"><Plus size={16} className="mr-1.5" /> Ajouter un parent</Button>}
      />
      {parents === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="parents-table">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Parent</th>
                <th className="px-5 py-3">Identifiant</th>
                <th className="px-5 py-3">E-mail</th>
                <th className="px-5 py-3 text-center">Enfants</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {parents.map((u) => (
                <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`parent-row-${u.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900"><div className="flex items-center gap-2"><Users size={15} className="text-slate-400" />{u.first_name} {u.last_name}</div></td>
                  <td className="px-5 py-3.5 text-slate-600">{u.identifiant}</td>
                  <td className="px-5 py-3.5 text-slate-600">{u.email || "—"}</td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{u.children_count}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={u.status} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => { setEditing(u); setDialogOpen(true); }} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-900" data-testid={`edit-parent-${u.id}`}><Pencil size={16} /></button>
                      <StatusMenu
                        current={u.status}
                        onChange={(st) => changeStatus(u, st)}
                        testid={`parent-menu-${u.id}`}
                        extra={<DropdownMenuItem onClick={() => resetPassword(u)} data-testid={`reset-parent-${u.id}`}><KeyRound size={15} className="mr-2" /> Réinitialiser le mot de passe</DropdownMenuItem>}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ParentDialog open={dialogOpen} onOpenChange={setDialogOpen} parent={editing} onSaved={load} />

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau mot de passe</DialogTitle>
            <DialogDescription>Communiquez ce mot de passe temporaire à {tempPw?.name}.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <code className="flex-1 text-lg font-semibold tracking-wide text-slate-900" data-testid="temp-password-value">{tempPw?.pw}</code>
            <button onClick={() => { navigator.clipboard.writeText(tempPw?.pw || ""); toast.success("Copié."); }} className="rounded-lg p-1.5 text-slate-500 hover:bg-white"><Copy size={16} /></button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
